import {CAMPAIGN_KEYS} from "@/api/campaignApi";
import {CAMPAIGN_APPLICATION_KEYS} from "@/api/campaignApplicationApi";
import {stripParamKeys} from "@/api/influencerApi";
import {USER_KEYS} from "@/api/userApi";
import type {
  CampaignManagementSerializer,
  CampaignManagementStatsSerializer,
  CampaignOwnerDetailSerializer,
  InvoiceSerializer,
} from "@/generated/DjangoTypes";
import {client} from "@/httpClient";
import {capturePostHogEvent, PostHogEvents} from "@/posthog";
import {queryClient} from "@/queryClient";
import useCampaignInfluencerFilterStore from "@/store/campaignInfluencerFilter";
import {create} from "@/store/createStore";
import {hasClosedPaymentFailedModalAtom, paymentFailedModalDataAtom} from "@/store/jotaiAtoms";
import type {
  TCampaignApplicationMinimal,
  TCampaignTab,
  TCampaignType,
  TNonFunctionKeys,
  TPackageName,
  TPaginated,
} from "@/store/types";
import useUserStore from "@/store/user";
import type {InfiniteData} from "@tanstack/react-query";
import type {AxiosResponse} from "axios";
import {getDefaultStore} from "jotai";
import {devtools} from "zustand/middleware";
import {immer} from "zustand/middleware/immer";

export type TReportingOrdering = "-performance" | "-created" | "-has_uncopied_code";

type TState = {
  campaign: CampaignOwnerDetailSerializer;
  management: CampaignManagementSerializer;
  reportingDateFilter: string | null;
  reportingOrdering: TReportingOrdering;
  tabState: TCampaignTab;
  isBonusPoolPopupOpen: boolean;
  isBonusPoolFlowActive: boolean;
  isCampaignStartPopupOpen: boolean;
  isTrialPopupOpen: boolean;
  isUgcCampaignStartPopupOpen: boolean;
  applicationToConfirm: TCampaignApplicationMinimal | null;
  applicationToRenegotiate: TCampaignApplicationMinimal | null;
  selectedSocialType: "instagram" | "tiktok";
  showRenegotiationDeclineButton: boolean;
  setValue<K extends TNonFunctionKeys<TState>>(key: K, value: TState[K]): void;
  acceptApplicant(applicationId: number): Promise<void>;
  declineApplicant(applicationId: number): Promise<void>;
  undoApplicant(applicationId: number): Promise<void>;
  startCampaign(applicationId?: number, isRetryAfterAuthentication?: boolean): Promise<void>;
  getCampaignType(): TCampaignType;
  getCompanyPlanName(): TPackageName | undefined;
  reset(): void;
};

const initialState = {
  tabState: "overview",
  management: {},
  campaign: {},
  isBonusPoolPopupOpen: false,
  isBonusPoolFlowActive: false,
  isCampaignStartPopupOpen: false,
  isTrialPopupOpen: false,
  isUgcCampaignStartPopupOpen: false,
  reportingDateFilter: null,
  reportingOrdering: "-performance",
  applicationToConfirm: null,
  applicationToRenegotiate: null,
  selectedSocialType: "instagram",
  showRenegotiationDeclineButton: true,
} as Pick<TState, TNonFunctionKeys<TState>>;

export const getCampaignApplicationQueryKeys = (campaignId: number) => {
  const searchParams = stripParamKeys(useCampaignInfluencerFilterStore.getState().searchParams ?? "");

  return [
    CAMPAIGN_KEYS.qCampaignAcceptedApplications(campaignId, `${searchParams}&pro=false`),
    CAMPAIGN_KEYS.qCampaignAcceptedApplications(campaignId, `${searchParams}&pro=true`),
    CAMPAIGN_KEYS.qCampaignApplicationCounts(campaignId, searchParams),
    CAMPAIGN_KEYS.qCampaignDeclinedApplications(campaignId, `${searchParams}&pro=false`),
    CAMPAIGN_KEYS.qCampaignDeclinedApplications(campaignId, `${searchParams}&pro=true`),
    CAMPAIGN_KEYS.qCampaignOpenApplications(campaignId, `${searchParams}&pro=false`),
    CAMPAIGN_KEYS.qCampaignOpenApplications(campaignId, `${searchParams}&pro=true`),
    CAMPAIGN_KEYS.qCampaignRenegotiations(campaignId, searchParams),
  ];
};

const removeApplicationFromLists = ({applicationId, campaignId}: {applicationId: number; campaignId: number}) =>
  getCampaignApplicationQueryKeys(campaignId).forEach(queryKey =>
    queryClient.setQueryData<InfiniteData<AxiosResponse<TPaginated<{id?: number}>>>>(queryKey, old =>
      old?.pages
        ? {
            ...old,
            pages: old.pages.map(page => ({
              ...page,
              data: {...page.data, results: page.data.results.filter(application => application.id !== applicationId)},
            })),
          }
        : undefined,
    ),
  );

const useCampaignManagementStore = create<TState>()(
  immer(
    devtools(
      (set, get) => ({
        ...initialState,
        setValue: (key, value) =>
          set(
            state => {
              state[key] = value;
            },
            false,
            "campaignManagementStore/setValue",
          ),
        acceptApplicant: async applicationId => {
          try {
            await client.post(CAMPAIGN_APPLICATION_KEYS.mApplicationAccept(+applicationId)[0]);
            const campaignId = get().campaign.id;
            removeApplicationFromLists({applicationId, campaignId});
            const companyId = get().campaign.company_profile?.id;
            const publishedAt = get().campaign.created;
            capturePostHogEvent(PostHogEvents.InfluencerAccepted, {
              campaignId,
              campaignType: get().getCampaignType(),
              companyId,
              influencerApplicationId: applicationId,
              plan: get().getCompanyPlanName(),
              publishedAt,
            });

            await Promise.all(
              [
                ...getCampaignApplicationQueryKeys(campaignId),
                CAMPAIGN_KEYS.qCampaignManagement(campaignId),
                CAMPAIGN_KEYS.qCampaignManagementStats(campaignId),
              ].map(queryKey => queryClient.refetchQueries({queryKey})),
            );
          } catch (error) {
            console.log(error);
          }
        },
        declineApplicant: async applicationId => {
          try {
            await client.post(CAMPAIGN_APPLICATION_KEYS.mApplicationDecline(+applicationId)[0]);
            const campaignId = get().campaign.id;
            removeApplicationFromLists({applicationId, campaignId});
            const companyId = get().campaign.company_profile?.id;
            const publishedAt = get().campaign.created;
            capturePostHogEvent(PostHogEvents.InfluencerDeclined, {
              companyId,
              campaignId,
              campaignType: get().getCampaignType(),
              plan: get().getCompanyPlanName(),
              publishedAt,
              influencerApplicationId: applicationId,
            });

            await Promise.all(
              [...getCampaignApplicationQueryKeys(campaignId), CAMPAIGN_KEYS.qCampaignManagement(campaignId)].map(
                queryKey => queryClient.refetchQueries({queryKey: queryKey}),
              ),
            );
          } catch (error) {
            console.log(error);
          }
        },
        undoApplicant: async applicationId => {
          try {
            await client.post(CAMPAIGN_APPLICATION_KEYS.mApplicationPending(+applicationId)[0]);
            const campaignId = get().campaign.id; // necessary to undo application
            removeApplicationFromLists({applicationId, campaignId});
            const companyId = get().campaign.company_profile?.id;
            const publishedAt = get().campaign.created;
            capturePostHogEvent(PostHogEvents.RevertInfluencerDecisionBackToPending, {
              companyId,
              campaignId,
              campaignType: get().getCampaignType(),
              plan: get().getCompanyPlanName(),
              publishedAt,
              influencerApplicationId: applicationId,
            });

            await Promise.all(
              [
                ...getCampaignApplicationQueryKeys(campaignId),
                CAMPAIGN_KEYS.qCampaignManagement(campaignId),
                CAMPAIGN_KEYS.qCampaignManagementStats(campaignId),
              ].map(queryKey => queryClient.refetchQueries({queryKey})),
            );
          } catch (error) {
            console.log(error);
          }
        },
        startCampaign: async (applicationId, isRetryAfterAuthentication) => {
          const campaignId = get().campaign.id;
          try {
            const res = await client.post(CAMPAIGN_KEYS.mCampaignStart(campaignId)[0], {
              success_url: window.location.href,
              cancel_url: window.location.href,
              campaign_application_id:
                applicationId && get().campaign.event && get().campaign.on_going ? applicationId : undefined,
            });

            if (res.data.stripe_url) {
              window.location.href = res.data.stripe_url;
              return;
            }

            const previousManagement = queryClient.getQueryData<AxiosResponse<CampaignManagementSerializer>>(
              CAMPAIGN_KEYS.qCampaignManagement(campaignId),
            )?.data;

            const previousAccepted =
              (previousManagement?.accepted_applications ?? 0) + (previousManagement?.accepted_applications_pro ?? 0);
            const expectedAccepted = applicationId ? previousAccepted - 1 : 0;

            const pollForUpdate = async (attempt = 0): Promise<void> => {
              const managementUrl = CAMPAIGN_KEYS.qCampaignManagement(campaignId)[0];
              const polled = (await client.get<CampaignManagementSerializer>(managementUrl)).data;
              const currentAccepted = (polled?.accepted_applications ?? 0) + (polled?.accepted_applications_pro ?? 0);

              if (currentAccepted > expectedAccepted && attempt < 10) {
                await new Promise(resolve => setTimeout(resolve, 500));
                return pollForUpdate(attempt + 1);
              }

              const user = useUserStore.getState().user;

              await Promise.all(
                [
                  ...getCampaignApplicationQueryKeys(campaignId),
                  CAMPAIGN_KEYS.qCampaign(campaignId),
                  CAMPAIGN_KEYS.qCampaignManagement(campaignId),
                  CAMPAIGN_KEYS.qCampaignManagementStats(campaignId),
                  CAMPAIGN_KEYS.qCampaignPreparationApplications(campaignId),
                  ...(user.package_subscription?.status === "trialing" ? [USER_KEYS.qUser(user.id)] : []),
                ].map(queryKey => queryClient.refetchQueries({queryKey})),
              );

              const management = polled;

              // PostHog event
              const companyId = get().campaign.company_profile?.id;
              const publishedAt = get().campaign.created;

              capturePostHogEvent(PostHogEvents.StartCampaign, {
                amountOfAcceptedInfluencersNoLimit: management?.accepted_applications_no_limit,
                amountOfAcceptedProInfluencers: management?.accepted_applications_pro,
                amountOfAcceptedRegularInfluencersIncludingNoLimit: management?.accepted_applications,
                amountOfActiveApplications: management?.active_applications,
                amountOfDeclinedInfluencersNoLimit: management?.declined_applications_no_limit,
                amountOfDeclinedProInfluencers: management?.declined_applications_pro,
                amountOfDeclinedRegularInfluencersIncludingNoLimit: management?.declined_applications,
                amountOfOpenApplicationsInfluencersNoLimit: management?.open_applications_no_limit,
                amountOfOpenApplicationsProInfluencers: management?.open_applications_pro,
                amountOfOpenApplicationsRegularInfluencers: management?.open_applications,
                amountOfPreparationApplications: management?.preparation_applications,
                amountOfReportingApplications: management?.reporting_applications,
                campaignId,
                campaignType: get().getCampaignType(),
                companyId,
                influencerApplicationId: applicationId,
                isEventCampaign: management?.event,
                isOngoingCampaign: management?.on_going,
                plan: get().getCompanyPlanName(),
                publishedAt,
              });

              if (!management?.open_applications && !management?.accepted_applications) {
                const searchParams = new URLSearchParams(window.location.search);
                searchParams.set("tab", get().campaign.preparation_days === 0 ? "active" : "preparation");
                window.history.pushState({}, "", `${window.location.pathname}?${searchParams.toString()}`);
                window.dispatchEvent(new Event("popstate"));
              }
            };

            pollForUpdate();

            set(
              state => {
                state.applicationToConfirm = null;
                state.isBonusPoolPopupOpen = false;
                state.isBonusPoolFlowActive = false;
                state.isCampaignStartPopupOpen = false;
                state.isTrialPopupOpen = false;
                state.isUgcCampaignStartPopupOpen = false;
              },
              false,
              "campaignManagementStore/startCampaign",
            );
          } catch (error: any) {
            const errorDetail = error.response?.data?.detail;

            if (errorDetail === "stripe_payment_requires_action" && !isRetryAfterAuthentication) {
              // The subscription's first charge needs on-session authentication
              // (SCA/3DS) — run the challenge and relaunch the campaign once.
              const clientSecret = error.response?.data?.client_secret;
              const {stripePromise} = await import("@/stripe");
              const stripe = await stripePromise;
              const confirmation = clientSecret && stripe ? await stripe.confirmCardPayment(clientSecret) : undefined;

              if (confirmation?.paymentIntent) {
                return get().startCampaign(applicationId, true);
              }
            }

            if (errorDetail === "failed_to_pay_invoice") {
              const stats = queryClient.getQueryData<AxiosResponse<CampaignManagementStatsSerializer>>(
                CAMPAIGN_KEYS.qCampaignManagementStats(campaignId),
              )?.data;
              const totalCost = Math.max(0, stats?.total_cost!);
              const failedPayments = (
                await client.get<TPaginated<InvoiceSerializer>>(`/invoices/failed_payments/?campaign_id=${campaignId}`)
              ).data.results;
              const failedInvoice = failedPayments?.find(
                invoice => invoice.invoice_type === "UGC_CAMPAIGN" && invoice.amount === totalCost,
              );

              if (failedInvoice) {
                getDefaultStore().set(paymentFailedModalDataAtom, [failedInvoice]);
                set(state => {
                  state.isCampaignStartPopupOpen = false;
                  state.isTrialPopupOpen = false;
                });
              }
            } else if (errorDetail === "stripe_payment_failed" || errorDetail === "stripe_payment_requires_action") {
              setTimeout(() => {
                getDefaultStore().set(hasClosedPaymentFailedModalAtom, false);
                const userId = useUserStore.getState().user.id;
                queryClient.invalidateQueries({queryKey: USER_KEYS.qUser(userId)});
              }, 2500);
              set(state => {
                state.isCampaignStartPopupOpen = false;
                state.isTrialPopupOpen = false;
              });
            }

            return Promise.reject(error);
          }
        },
        getCampaignType: () => (get().campaign.ugc ? "ugc" : get().campaign.tiktok ? "tiktok" : "instagram"),
        getCompanyPlanName: () => useUserStore.getState().user.package_subscription?.name,
        reset: () => set(initialState, false, "campaignManagementStore/reset"),
      }),
      {
        name: "useCampaignManagementStore",
      },
    ),
  ),
);

export default useCampaignManagementStore;

export const useManagedCampaignType: () => TCampaignType = () =>
  useCampaignManagementStore(state => (state.campaign.tiktok ? "tiktok" : state.campaign.ugc ? "ugc" : "instagram"));

export const useRequiredCampaignContentCount = () => {
  const requiredContent = useRequiredCampaignContent();
  const campaignType = useManagedCampaignType();

  const requiredContentCounts = {
    ...requiredContent,
    instagram: requiredContent.instagram.posts + requiredContent.instagram.reels + requiredContent.instagram.stories,
  };

  return requiredContentCounts[campaignType];
};

export const useRequiredCampaignContent = () => {
  const campaign = useManagementCampaign();

  const requiredContent = {
    ugc: campaign.number_of_ugc || 0,
    instagram: {
      stories: campaign.number_of_stories || 0,
      reels: campaign.number_of_reels || 0,
      posts: campaign.number_of_feedposts || 0,
    },
    tiktok: campaign.tiktok_number_of_medias || 0,
  };
  return requiredContent;
};

export const useManagementCampaign = () => useCampaignManagementStore(state => state.campaign);
